import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { makeRequest } from "../../utils/request";

export interface Notification {
  id: string;
  userId: string;
  organizationId?: string;
  type: string;
  category: string;
  title: string;
  message: string;
  metadata?: Record<string, any>;
  actionUrl?: string;
  readAt?: string;
  deletionPolicy: "delete_on_read" | "delete_later";
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
}

export interface NotificationsResponse {
  data: Notification[];
  unreadCount: number;
  totalUnreadCount: number;
}

export interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  totalUnreadCount: number; // For bell indicator - total across all categories
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  error: string | null;
  filters: {
    category?: string;
    unread?: boolean;
  };
  pagination: {
    page: number;
    limit: number;
  };
}

const initialState: NotificationState = {
  notifications: [],
  unreadCount: 0,
  totalUnreadCount: 0,
  isLoading: false,
  isLoadingMore: false,
  hasMore: true,
  error: null,
  filters: {},
  pagination: {
    page: 1,
    limit: 50,
  },
};

// Fetch notifications
export const fetchNotifications = createAsyncThunk(
  "notifications/fetch",
  async (params?: { category?: string; unread?: boolean; page?: number; limit?: number }) => {
    const queryParams = new URLSearchParams();
    if (params?.category) queryParams.append("category", params.category);
    if (params?.unread) queryParams.append("unread", "true");
    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.limit) queryParams.append("limit", params.limit.toString());

    const response = await makeRequest<NotificationsResponse>(`/v1/notifications${queryParams.toString() ? `?${queryParams.toString()}` : ""}`);
    return response;
  },
);

// Mark notification as read
export const markNotificationAsRead = createAsyncThunk("notifications/markRead", async (notificationId: string) => {
  await makeRequest(`/v1/notifications/${notificationId}/read`, {
    method: "PATCH",
  });
  return notificationId;
});

// Mark all notifications as read
export const markAllAsRead = createAsyncThunk("notifications/markAllRead", async () => {
  await makeRequest(`/v1/notifications/mark-all-read`, {
    method: "POST",
  });
});

// Delete notification
export const deleteNotification = createAsyncThunk("notifications/delete", async (notificationId: string) => {
  await makeRequest(`/v1/notifications/${notificationId}`, {
    method: "DELETE",
  });
  return notificationId;
});

const notificationSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<{ category?: string; unread?: boolean }>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    setPagination: (state, action: PayloadAction<{ page: number; limit?: number }>) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    clearError: (state) => {
      state.error = null;
    },
    addNotification: (state, action: PayloadAction<Notification>) => {
      state.notifications.unshift(action.payload);
      if (!action.payload.readAt) {
        state.unreadCount++;
        state.totalUnreadCount++;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch notifications
      .addCase(fetchNotifications.pending, (state, action) => {
        const page = action.meta.arg?.page ?? 1;
        if (page > 1) {
          state.isLoadingMore = true;
        } else {
          state.isLoading = true;
        }
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        const page = action.meta.arg?.page ?? 1;
        const limit = action.meta.arg?.limit ?? state.pagination.limit;
        const incoming = action.payload.data;

        if (page > 1) {
          const existingIds = new Set(state.notifications.map((n) => n.id));
          state.notifications = [...state.notifications, ...incoming.filter((n) => !existingIds.has(n.id))];
        } else {
          state.notifications = incoming;
        }

        state.unreadCount = action.payload.unreadCount;
        state.totalUnreadCount = action.payload.totalUnreadCount;
        state.hasMore = incoming.length >= limit;
        state.pagination.page = page;
        state.isLoading = false;
        state.isLoadingMore = false;
        state.error = null;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.isLoading = false;
        state.isLoadingMore = false;
        state.error = action.error.message || "Failed to fetch notifications";
      })
      // Mark as read
      .addCase(markNotificationAsRead.fulfilled, (state, action) => {
        const notification = state.notifications.find((n) => n.id === action.payload);
        if (notification && !notification.readAt) {
          notification.readAt = new Date().toISOString();
          state.unreadCount = Math.max(0, state.unreadCount - 1);
          state.totalUnreadCount = Math.max(0, state.totalUnreadCount - 1);
        }
      })
      // Mark all as read
      .addCase(markAllAsRead.fulfilled, (state) => {
        state.notifications.forEach((notification) => {
          if (!notification.readAt) {
            notification.readAt = new Date().toISOString();
          }
        });
        state.unreadCount = 0;
        state.totalUnreadCount = 0;
      })
      // Delete notification
      .addCase(deleteNotification.fulfilled, (state, action) => {
        const deleted = state.notifications.find((n) => n.id === action.payload);
        if (deleted && !deleted.readAt) {
          state.unreadCount = Math.max(0, state.unreadCount - 1);
          state.totalUnreadCount = Math.max(0, state.totalUnreadCount - 1);
        }
        state.notifications = state.notifications.filter((notification) => notification.id !== action.payload);
      });
  },
});

export const { setFilters, setPagination, clearError, addNotification } = notificationSlice.actions;
export default notificationSlice.reducer;
